import { Component, inject, input, OnInit, output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { COUNTRIES_CODE } from './lang.const';
import { detectCountryFromTimezone } from './country-detection.util';
import {
  ReactiveFormsModule,
  FormBuilder,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmSelectImports } from '@spartan-ng/helm/select';
import { ThemedIconComponent } from '../../atoms/themed-icon/themed-icon.component';
import { CONNECT_PORTAL_URL } from 'shared';
import { buildSignupLegalTextHtml } from './legal-documents.config';
import { isValidEmail } from 'utilities';

@Component({
  selector: 'ocp-signup-form',
  templateUrl: './signup-form.component.html',
  styleUrls: ['./signup-form.component.scss'],
  imports: [
    ReactiveFormsModule,
    HlmButtonImports,
    HlmCardImports,
    HlmSelectImports,
    ThemedIconComponent,
  ],
})
export class SignupFormComponent implements OnInit {
  readonly submitted = output<
    Partial<{
      email: string | null;
      firstName: string | null;
      lastName: string | null;
      password: string | null;
      confirmPassword: string | null;
      country: string | null;
      legal: boolean | null;
    }>
  >();

  readonly prefilledEmail = input('');
  readonly errorMessage = input<string | null>(null);

  private readonly fb = inject(FormBuilder);
  private readonly translate = inject(TranslateService);
  readonly countryCodes = COUNTRIES_CODE;
  readonly countryOptions: { label: string; value: string }[] = this.countryCodes.map(c => ({
    label: this.translate.instant('countries.' + c),
    value: c,
  }));

  private readonly connectPortalUrl = inject(CONNECT_PORTAL_URL);
  private readonly detectedCountry = detectCountryFromTimezone();

  readonly legalTextHtml = buildSignupLegalTextHtml(this.connectPortalUrl);

  get sanitizedLegalTextPlain(): string {
    // strip HTML tags for aria-label / accessibility text
    return this.legalTextHtml
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  form = this.fb.group(
    {
      email: ['', [Validators.required, SignupFormComponent.emailValidator]],
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      password: ['', [Validators.required, Validators.minLength(8)]],
      confirmPassword: ['', [Validators.required]],
      country: [this.detectedCountry, [Validators.required]],
      legal: [false, [Validators.requiredTrue]],
    },
    { validators: this.passwordsMatch }
  );
  showPassword = false;
  showConfirmPassword = false;

  ngOnInit() {
    const email = this.prefilledEmail();
    if (email) {
      this.form.controls.email.setValue(email);
      this.form.controls.email.disable();
    }
  }

  toggleShowPassword() {
    this.showPassword = !this.showPassword;
  }

  toggleShowConfirmPassword() {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  get f() {
    return this.form.controls;
  }

  onCountryChange(value: string | null | undefined) {
    this.form.controls.country.setValue(value ?? null);
    this.form.controls.country.markAsTouched();
  }

  // Angular's built-in Validators.email is ASCII-only and rejects valid addresses
  // with Unicode (e.g. Japanese) local parts; isValidEmail accepts them.
  private static emailValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    return !value || isValidEmail(value) ? null : { email: true };
  }

  passwordsMatch(group: AbstractControl | null) {
    if (!group) return null;
    const pw = group.get('password')?.value;
    const cp = group.get('confirmPassword')?.value;
    return pw && cp && pw !== cp ? { passwordsMismatch: true } : null;
  }

  onSubmit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.submitted.emit(this.form.getRawValue());
  }
}
